import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Button,
  Table,
  Input,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { flattenServicesByType, getPaymentBadgeColor } from '../../utils/bookingOrderHelpers';
import BookingStatusSelect from '../../components/Bookings/BookingStatusSelect';
import { useScopedOrders } from '../../hooks/useScopedOrders';

const ToursList: React.FC = () => {
  document.title = 'Tours | NGK Tours';
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { orders, loading, error, refetch } = useScopedOrders({ first: 100 });

  const rows = useMemo(
    () => flattenServicesByType(orders, 'tour'),
    [orders]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.subOrderNo, r.summary.orderNo, r.summary.customerName, r.packageName, r.summary.status]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Bookings" breadcrumbItem="Tours" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <p className="text-muted mb-0">Tour packages from WooCommerce orders.</p>
                    <div className="d-flex gap-2">
                      <Input
                        type="search"
                        placeholder="Search tour..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ minWidth: 220 }}
                      />
                      <Button color="secondary" size="sm" outline onClick={() => refetch()}>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {error && (
                    <Alert color="danger" className="mb-3">
                      {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
                        ?.message || error.message}
                    </Alert>
                  )}

                  {loading && !rows.length ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading tours...</p>
                    </div>
                  ) : filtered.length === 0 ? (
                    <Alert color="info" className="mb-0">
                      No tour bookings found.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-bordered table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Order No</th>
                            <th>Customer Name</th>
                            <th>Tour Booking</th>
                            <th>Booking Date</th>
                            <th>Tour Date</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Payment</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((row) => (
                            <tr key={`${row.summary.order.databaseId}-${row.subOrderNo}`}>
                              <td className="fw-medium">{row.summary.orderNo}</td>
                              <td>{row.summary.customerName}</td>
                              <td>{row.packageName}</td>
                              <td>{row.summary.bookingDate}</td>
                              <td>{row.travelDate}</td>
                              <td>
                                <BookingStatusSelect
                                  orderId={row.summary.order.databaseId}
                                  status={row.summary.status}
                                />
                              </td>
                              <td className="fw-medium">{row.summary.amount}</td>
                              <td>
                                <Badge color={getPaymentBadgeColor(row.summary.paymentStatus)}>
                                  {row.summary.paymentStatus}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/bookings/${row.summary.order.databaseId}`)
                                  }
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ToursList;
